import React, { useEffect, useCallback } from 'react';
import { Box } from '@mui/material';
import { Button, colors, Typography } from '@sweep-io/sweep-design';
import { ArtifactData } from '../ai-chat/aiChatTypes';
import { useArtifacts, simulateArtifactStreaming } from './useArtifacts';
import { getContentType, getDemoArtifactContent } from '../../helpers/artifactHelpers';
import { useIsCanvasUnited } from '../../../hooks/useIsCanvasUnited';
import useAgentsHome from '../../aiAgentCenter/useAgentsHome';

interface ArtifactPreviewProps {
  artifactData: {
    artifactId: string;
    title: string;
    subtitle?: string;
  };
}

export const ArtifactPreview: React.FC<ArtifactPreviewProps> = ({ artifactData }) => {
  const {
    openExistingArtifact,
    startArtifactStreaming,
    addArtifactData,
    addStreamingContent,
    completeArtifactStreaming,
    artifacts,
    streamingContent,
    isStreamingComplete,
    currentArtifactId,
    setStreamingComplete,
  } = useArtifacts();

  const isCanvasUnited = useIsCanvasUnited();
  const { setIsExpanded } = useAgentsHome();

  const openOrBuildArtifact = useCallback(() => {
    // Canvas-specific: expand agent panel to full screen when artifact opens
    if (isCanvasUnited) {
      setIsExpanded({ isExpanded: true });
    }

    const existingArtifact = artifacts[artifactData.artifactId];

    if (existingArtifact) {
      // Artifact already exists - just open the panel
      openExistingArtifact(artifactData.artifactId);
    } else {
      // Artifact doesn't exist - start streaming
      startArtifactStreaming(artifactData.artifactId);

      // Get the demo content based on artifact ID
      const artifactContent = getDemoArtifactContent(artifactData.artifactId);

      // Start the streaming simulation
      simulateArtifactStreaming(
        artifactData.artifactId,
        artifactContent,
        addStreamingContent, // Accumulate chunks
        () => setStreamingComplete(true),
      );
    }
  }, [
    artifactData.artifactId,
    artifacts,
    openExistingArtifact,
    startArtifactStreaming,
    addStreamingContent,
    setStreamingComplete,
    isCanvasUnited,
    setIsExpanded,
  ]);

  useEffect(() => {
    if (isStreamingComplete && streamingContent && currentArtifactId === artifactData.artifactId) {
      const detectedContentType = getContentType(streamingContent);

      // Use the ACTUAL accumulated streaming content
      const finalArtifactData: ArtifactData = {
        artifactId: artifactData.artifactId,
        versions: [
          {
            versionId: '1',
            title: artifactData.title,
            subtitle: artifactData.subtitle,
            content: streamingContent,
            contentType: detectedContentType,
            createdAt: new Date().toISOString(),
          },
        ],
        currentVersion: '1',
      };

      addArtifactData(artifactData.artifactId, finalArtifactData);
      completeArtifactStreaming();
    }
  }, [
    isStreamingComplete,
    streamingContent,
    currentArtifactId,
    artifactData.artifactId,
    artifactData.title,
    artifactData.subtitle,
    addArtifactData,
    completeArtifactStreaming,
  ]);

  // Auto-open panel when artifact message appears (only for new artifacts)
  useEffect(() => {
    const existingArtifact = artifacts[artifactData.artifactId];
    if (!existingArtifact) {
      openOrBuildArtifact();
    }
  }, [openOrBuildArtifact, artifacts, artifactData.artifactId]);

  const handleViewDetails = () => {
    openOrBuildArtifact();
  };

  return (
    <Box
      sx={{
        border: `1px solid ${colors.grey[300]}`,
        borderRadius: '12px',
        padding: '20px',
        backgroundColor: 'white',
        width: '100%',
        transition: 'all 0.2s ease-in-out',
        cursor: 'pointer',
      }}
      onClick={handleViewDetails}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="body">{artifactData.title}</Typography>

          {artifactData.subtitle && (
            <Typography variant="caption" color={colors.grey[700]}>
              {artifactData.subtitle}
            </Typography>
          )}

          <Button endIconName="ChevronRight" onClick={handleViewDetails} variant="flat-blue">
            View details
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
